import React, { useState } from 'react';
import { CalendarOff, Clock, Trash2, RotateCcw, Plus, Bell, AlertCircle, Eye, Pencil } from 'lucide-react';

const KIND_OPTIONS = [
  { value: 'closed', label: 'Closed' },
  { value: 'late_open', label: 'Opening late' },
  { value: 'early_close', label: 'Closing early' },
];

const STATE_BADGE = {
  active: 'bg-red-100 text-red-700 ring-red-200',
  scheduled: 'bg-amber-100 text-amber-800 ring-amber-200',
  ended: 'bg-gray-100 text-gray-600 ring-gray-200',
  past: 'bg-gray-100 text-gray-500 ring-gray-200',
};

const emptyForm = {
  kind: 'closed', timing: 'now',
  startDate: '', startTime: '', endDate: '', endTime: '', time: '',
  reason: '', notifyFollowers: false,
};

// --- Europe/Madrid clock (closures are evaluated in the business region) ------
const MON_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

const madridNow = () => {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const g = (t) => p.find((x) => x.type === t).value;
  let hh = g('hour'); if (hh === '24') hh = '00';
  return { date: `${g('year')}-${g('month')}-${g('day')}`, time: `${hh}:${g('minute')}` };
};
const localToday = () => madridNow().date;

const dayLabel = (iso, todayIso) => {
  const d = parseISO(iso), today = parseISO(todayIso);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  return `${WD[(d.getDay() + 6) % 7]} ${d.getDate()} ${MON_S[d.getMonth()]}`;
};
const dateOnly = (iso) => { const d = parseISO(iso); return `${d.getDate()} ${MON_S[d.getMonth()]}`; };

// Mirrors backend services/business_status._closure_view (public wording).
const describeWindow = (sd, st, ed, et, kind, phase, today) => {
  if (kind === 'late_open') {
    const d = dayLabel(sd, today);
    return phase === 'active' ? `Opening late ${d} — from ${et}` : `Opening late ${d} at ${et}`;
  }
  if (kind === 'early_close') {
    const d = dayLabel(sd, today);
    if (phase === 'active') return d === 'today' ? 'Closed early today' : `Closed early ${d}`;
    return `Closing early ${d} at ${st}`;
  }
  if (sd === ed) {
    if (st === '00:00' && et === '23:59') {
      return phase === 'active' && sd === today ? 'Temporarily closed today' : `Closed ${dayLabel(sd, today)}`;
    }
    return `Closed ${dayLabel(sd, today)}, ${st}–${et}`;
  }
  const left = dateOnly(sd) + (st === '00:00' ? '' : ` ${st}`);
  const right = dateOnly(ed) + (et === '23:59' ? '' : ` ${et}`);
  return `Closed from ${left} until ${right}`;
};

// Build the {sd,st,ed,et,kind} window from the current form.
const buildWindow = (form) => {
  const today = localToday();
  if (form.kind === 'closed') {
    const sd = form.timing === 'now' ? today : form.startDate;
    const st = form.timing === 'now' ? madridNow().time : (form.startTime || '00:00');
    return { sd, st, ed: form.endDate, et: form.endTime || '23:59', kind: 'closed' };
  }
  if (form.kind === 'late_open') {
    return { sd: form.startDate, st: '00:00', ed: form.startDate, et: form.time, kind: 'late_open' };
  }
  return { sd: form.startDate, st: form.time, ed: form.startDate, et: '23:59', kind: 'early_close' };
};

const previewMessage = (form) => {
  const w = buildWindow(form);
  if (!w.sd || !w.ed) return null;
  if (w.kind === 'closed' && !form.endDate) return null;
  if ((w.kind === 'late_open' || w.kind === 'early_close') && !form.time) return null;
  const now = madridNow();
  const nowStr = `${now.date}T${now.time}`;
  const startStr = `${w.sd}T${w.st}`;
  const endStr = `${w.ed}T${w.et}`;
  if (endStr <= startStr) return null;
  let phase;
  if (nowStr < startStr) phase = 'upcoming';
  else if (nowStr <= endStr) phase = 'active';
  else return null;
  return describeWindow(w.sd, w.st, w.ed, w.et, w.kind, phase, localToday());
};

// Owner-facing absolute summary for the history line (kind-aware, legacy-safe).
const describeClosure = (c) => {
  const kind = c.kind;
  if (kind === 'late_open') return `Opening late on ${c.startDate} (from ${c.endTime})`;
  if (kind === 'early_close') return `Closing early on ${c.startDate} (at ${c.startTime})`;
  if (kind === 'closed') {
    if (c.startDate === c.endDate) {
      if (c.startTime === '00:00' && c.endTime === '23:59') return `Closed on ${c.startDate}`;
      return `Closed ${c.startDate}, ${c.startTime}–${c.endTime}`;
    }
    return `Closed ${c.startDate} ${c.startTime} → ${c.endDate} ${c.endTime}`;
  }
  // legacy schema fallback
  if (c.type === 'single') return `Closed on ${c.startDate}`;
  if (c.type === 'range') return `Closed ${c.startDate} → ${c.endDate}`;
  if (c.type === 'partial') {
    if (c.partialMode === 'late_open') return `Opening late on ${c.startDate} (from ${c.fromTime})`;
    if (c.partialMode === 'early_close') return `Closing early on ${c.startDate} (at ${c.toTime})`;
    if (c.partialMode === 'closed_period') return `Closed ${c.fromTime}–${c.toTime} on ${c.startDate}`;
  }
  return 'Closure';
};

export const ClosureSection = ({ closures = [], loading, submitting, onCreate, onUpdate, onDelete, onReopen }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setError(''); };

  const startEdit = (c) => {
    const kind = c.kind || (c.type === 'partial'
      ? (c.partialMode === 'late_open' ? 'late_open' : c.partialMode === 'early_close' ? 'early_close' : 'closed')
      : 'closed');
    setForm({
      kind,
      timing: 'schedule',
      startDate: c.startDate || '',
      startTime: c.startTime || '',
      endDate: c.endDate || '',
      endTime: c.endTime || '',
      time: kind === 'late_open' ? (c.endTime || '') : kind === 'early_close' ? (c.startTime || '') : '',
      reason: c.reason || '',
      notifyFollowers: !!c.notifyFollowers,
    });
    setEditingId(c.id);
    setShowForm(true);
    setError('');
  };

  const submit = async () => {
    setError('');
    const w = buildWindow(form);
    if (form.kind === 'closed') {
      if (form.timing === 'schedule' && !form.startDate) return setError('Please choose a start date.');
      if (!form.endDate) return setError('Please choose an end date.');
    } else if (!form.startDate || !form.time) {
      return setError(form.kind === 'late_open' ? 'Please set the date and opening time.' : 'Please set the date and closing time.');
    }
    if (`${w.ed}T${w.et}` <= `${w.sd}T${w.st}`) return setError('The closure must end after it starts.');

    const payload = {
      kind: form.kind, timing: form.timing,
      startDate: w.sd, startTime: w.st, endDate: w.ed, endTime: w.et,
      reason: form.reason, notifyFollowers: form.notifyFollowers,
    };
    const ok = editingId ? await onUpdate(editingId, payload) : await onCreate(payload);
    if (ok) { resetForm(); setShowForm(false); }
    else setError('Could not save the closure. Please check the details and try again.');
  };

  const preview = previewMessage(form);
  const today = localToday();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-testid="closure-section">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <CalendarOff className="h-5 w-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Business Status &amp; Temporary Closures</h2>
        </div>
        <button
          onClick={() => { if (showForm) { setShowForm(false); resetForm(); } else { resetForm(); setShowForm(true); } }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
          data-testid="closure-add-toggle"
        >
          <Plus className="h-4 w-4" /> Set status
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">Mark your business temporarily closed with a clear start and end — for holidays, emergencies, illness, maintenance or bad weather. It overrides your opening hours for the selected period only and clears automatically when it ends. Your normal opening hours are never changed.</p>

      {showForm && (
        <div className="mb-5 rounded-lg border border-gray-200 p-4 space-y-3 bg-gray-50" data-testid="closure-form">
          <p className="text-sm font-semibold text-gray-800" data-testid="closure-form-title">
            {editingId ? 'Edit status / closure' : 'New status / closure'}
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">What's happening?</label>
            <div className="flex flex-wrap gap-2">
              {KIND_OPTIONS.map((o) => (
                <button key={o.value} type="button" onClick={() => set('kind', o.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${form.kind === o.value ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`closure-kind-${o.value}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {form.kind === 'closed' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">When does it start?</label>
              <div className="flex flex-wrap gap-2">
                {[{ value: 'now', label: 'Go live now' }, { value: 'schedule', label: 'Schedule for later' }].map((o) => (
                  <button key={o.value} type="button" onClick={() => set('timing', o.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${form.timing === o.value ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    data-testid={`closure-timing-${o.value}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CLOSED: from/to date + time */}
          {form.kind === 'closed' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {form.timing === 'schedule' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Closed from — date</label>
                    <input type="date" value={form.startDate} min={today} onChange={(e) => set('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="closure-start-date" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">From — time <span className="text-gray-400">(optional)</span></label>
                    <input type="time" value={form.startTime} onChange={(e) => set('startTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="closure-start-time" />
                  </div>
                </>
              )}
              {form.timing === 'now' && (
                <p className="text-xs text-gray-500 sm:col-span-2 -mt-1">Starts <strong>now</strong>. Choose when it ends below.</p>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Closed until — date</label>
                <input type="date" value={form.endDate} min={form.startDate || today} onChange={(e) => set('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="closure-end-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Until — time <span className="text-gray-400">(optional)</span></label>
                <input type="time" value={form.endTime} onChange={(e) => set('endTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="closure-end-time" />
              </div>
            </div>
          )}

          {/* LATE OPEN / EARLY CLOSE: single date + one time */}
          {(form.kind === 'late_open' || form.kind === 'early_close') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                <input type="date" value={form.startDate} min={today} onChange={(e) => set('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="closure-start-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{form.kind === 'late_open' ? 'Opening at' : 'Closing at'}</label>
                <input type="time" value={form.time} onChange={(e) => set('time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="closure-single-time" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason / message (optional)</label>
            <input type="text" value={form.reason} maxLength={200} onChange={(e) => set('reason', e.target.value)}
              placeholder="e.g. Closed for a local holiday"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="closure-reason" />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.notifyFollowers} onChange={(e) => set('notifyFollowers', e.target.checked)} data-testid="closure-notify" />
            <Bell className="h-4 w-4 text-purple-600" /> Notify my followers now
          </label>

          {preview && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="closure-preview">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" /> Customers will see
              </p>
              <p className="text-sm font-semibold text-blue-900 mt-0.5" data-testid="closure-preview-message">{preview}</p>
              {form.reason.trim() && (
                <p className="text-sm text-blue-800/90" data-testid="closure-preview-reason">{form.reason.trim()}</p>
              )}
            </div>
          )}

          {error && <p className="text-sm text-red-600 flex items-center gap-1" data-testid="closure-error"><AlertCircle className="h-4 w-4" /> {error}</p>}

          <div className="flex gap-2 pt-1">
            <button onClick={submit} disabled={submitting}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60"
              data-testid="closure-submit">
              {submitting ? 'Saving…' : (editingId ? 'Update status' : 'Save status')}
            </button>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* History */}
      {loading ? (
        <p className="text-sm text-gray-400 py-3">Loading…</p>
      ) : closures.length === 0 ? (
        <p className="text-sm text-gray-400 py-3" data-testid="closure-empty">No closures set. Your normal opening hours apply.</p>
      ) : (
        <ul className="space-y-2" data-testid="closure-list">
          {closures.map((c) => (
            <li key={c.id} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50" data-testid={`closure-item-${c.id}`}>
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{describeClosure(c)}</p>
                  {c.reason && <p className="text-xs text-gray-500">{c.reason}</p>}
                  {c.publicMessage ? (
                    <p className="text-xs text-blue-700 mt-0.5" data-testid={`closure-public-preview-${c.id}`}>
                      <span className="font-semibold">Customers see:</span> {c.publicMessage}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-0.5" data-testid={`closure-public-preview-${c.id}`}>Not shown to customers</p>
                  )}
                  <span className={`inline-block mt-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${STATE_BADGE[c.statusLabel] || STATE_BADGE.past}`} data-testid={`closure-state-${c.id}`}>
                    {c.statusLabel}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {c.statusLabel !== 'past' && c.statusLabel !== 'ended' && (
                  <button onClick={() => startEdit(c)} title="Edit" aria-label="Edit closure"
                    className="p-1.5 rounded-lg text-purple-700 hover:bg-purple-50" data-testid={`closure-edit-${c.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
                {c.statusLabel === 'active' && (
                  <button onClick={() => onReopen(c.id)} title="Reopen early" aria-label="Reopen early"
                    className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-50" data-testid={`closure-reopen-${c.id}`}>
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
                <button onClick={() => onDelete(c.id)} title="Remove" aria-label="Remove closure"
                  className="p-1.5 rounded-lg text-red-600 hover:bg-red-50" data-testid={`closure-delete-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ClosureSection;
